import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import './Logs.css';

const Logs = ({ setMessage: setMessageProp }) => {
  const setMsg = (...args) => {
    if (typeof setMessageProp === "function") return setMessageProp(...args);
  };

  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [logSearchQuery, setLogSearchQuery] = useState("");
  const [methodFilter, setMethodFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState("all");
  const [knownUsersFilter, setKnownUsersFilter] = useState(false);
  const [pathFilter, setPathFilter] = useState("all");
  const [durationFilter, setDurationFilter] = useState("all");
  const [flagFilter, setFlagFilter] = useState("all");
  const [currentLogPage, setCurrentLogPage] = useState(1);
  const [logsPerPage, setLogsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: "timestamp", direction: "desc" });
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [totalLogs, setTotalLogs] = useState(0);
  const [totalPagesLogs, setTotalPagesLogs] = useState(0);
  const [modalLogId, setModalLogId] = useState(null); // Track which log's headers to show in modal

  const statusCodeMessages = {
    200: "OK - The request was successful.",
    201: "Created - The resource was successfully created.",
    204: "No Content - The request was successful but there’s no content to return.",
    400: "Bad Request - The server couldn’t understand the request due to invalid syntax.",
    401: "Unauthorized - Authentication is required and has failed or not been provided.",
    403: "Forbidden - The client does not have access rights to the content.",
    404: "Not Found - The server can’t find the requested resource.",
    429: "Too Many Requests - The user has sent too many requests in a given amount of time.",
    500: "Internal Server Error - The server encountered an unexpected condition.",
    502: "Bad Gateway - The server received an invalid response from an upstream server.",
    503: "Service Unavailable - The server is temporarily unable to handle the request.",
    504: "Gateway Timeout - The server did not receive a timely response from an upstream server.",
  };

  useEffect(() => {
    fetchLogs();
  }, [timeFilter, logsPerPage, currentLogPage, statusFilter]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (logSearchQuery.trim()) setTimeFilter("all");
      setCurrentLogPage(1);
      fetchLogs();
    }, 350);
    return () => clearTimeout(t);
  }, [logSearchQuery]);

  useEffect(() => {
    const serverIsSearching = !!logSearchQuery.trim();
    applyFilters(
      logs,
      serverIsSearching ? "" : logSearchQuery,
      methodFilter,
      timeFilter,
      knownUsersFilter,
      pathFilter,
      durationFilter,
      flagFilter
    );
  }, [logSearchQuery, methodFilter, knownUsersFilter, pathFilter, durationFilter, sortConfig, logs, flagFilter, timeFilter]);

  const fetchLogs = async () => {
    setLoadingLogs(true);
    try {
      const response = await axios.get(
        `/api/logs?time=${timeFilter}&page=${currentLogPage}&limit=${logsPerPage}&status=${statusFilter}&search=${encodeURIComponent(logSearchQuery || "")}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      const { logs: logsData, totalLogs, totalPages } = response.data;
      const logsArray = Array.isArray(logsData) ? logsData : [];
      setLogs(logsArray);
      setTotalLogs(totalLogs);
      setTotalPagesLogs(totalPages);
    } catch (error) {
      console.error("Error fetching logs:", error);
      setMsg({ text: "Error loading logs.", type: "error" });
      setLogs([]);
      setFilteredLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setMsg({ text: "Request ID copied", type: "success" });
    } catch {
      setMsg({ text: "Failed to copy", type: "error" });
    }
  };

  const openHeadersModal = (id) => {
    setModalLogId(id);
  };

  const closeHeadersModal = () => {
    setModalLogId(null);
  };

  const applyFilters = (
    logsData,
    search = "",
    method = "all",
    _time = "all",
    knownUsers = false,
    path = "all",
    duration = "all",
    flag = "all"
  ) => {
    const noFiltersActive =
      !search &&
      method === "all" &&
      !knownUsers &&
      path === "all" &&
      duration === "all" &&
      flag === "all";

    if (noFiltersActive) {
      setFilteredLogs(Array.isArray(logsData) ? logsData : []);
      return;
    }

    let filtered = Array.isArray(logsData) ? [...logsData] : [];

    filtered = filtered.filter(log =>
      !(log.path?.startsWith('/tokens/') || log.path?.startsWith('/messages/'))
    );

    if (search) {
      const q = search.trim().toLowerCase();
      filtered = filtered.filter((log) => {
        const ua = log.headers?.['user-agent'] || "";
        const ref = log.headers?.['referer'] || "";
        return (
          (log.path?.toLowerCase() || "").includes(q) ||
          (log.ipAddress?.toLowerCase() || "").includes(q) ||
          (log.username?.toLowerCase() || "").includes(q) ||
          ua.toLowerCase().includes(q) ||
          ref.toLowerCase().includes(q) ||
          (log.requestId?.toLowerCase() || "").includes(q)
        );
      });
    }

    if (method !== "all") filtered = filtered.filter((log) => log.method === method);
    if (knownUsers) filtered = filtered.filter((log) => log.username);
    if (path !== "all") filtered = filtered.filter((log) => log.path === path);

    if (duration !== "all") {
      if (duration === "1000+") {
        filtered = filtered.filter((log) => log.duration >= 1000);
      } else {
        const [min, max] = duration.split("-").map(Number);
        filtered = filtered.filter((log) => log.duration >= min && log.duration <= max);
      }
    }

    if (flag !== "all") filtered = filtered.filter((log) => (log.suspicionFlags || []).includes(flag));

    filtered.sort((a, b) => {
      if (sortConfig.key === "timestamp") {
        return sortConfig.direction === "asc"
          ? new Date(a.timestamp) - new Date(b.timestamp)
          : new Date(b.timestamp) - new Date(a.timestamp);
      } else if (["status", "duration"].includes(sortConfig.key)) {
        return sortConfig.direction === "asc"
          ? (a[sortConfig.key] || 0) - (b[sortConfig.key] || 0)
          : (b[sortConfig.key] || 0) - (a[sortConfig.key] || 0);
      }
      return 0;
    });

    setFilteredLogs(filtered);
  };

  return (
    <div>
      <h3>API Logs</h3>

      <div className="logs-filter-container">
        <input
          type="text"
          placeholder="Search path, IP, username, UA, referer, or Request ID..."
          value={logSearchQuery}
          onChange={(e) => setLogSearchQuery(e.target.value)}
          className="logs-search-input"
        />

        <select
          value={methodFilter}
          onChange={(e) => setMethodFilter(e.target.value)}
          className="logs-filter-select"
        >
          <option value="all">All Methods</option>
          <option value="GET">GET</option>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="DELETE">DELETE</option>
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="logs-filter-select"
        >
          <option value="all">All Statuses</option>
          <option value="200-299">Success (200-299)</option>
          <option value="400-499">Client Errors (400-499)</option>
          <option value="500-599">Server Errors (500-599)</option>
        </select>

        <select
          value={timeFilter}
          onChange={(e) => setTimeFilter(e.target.value)}
          className="logs-filter-select"
        >
          <option value="all">All Time</option>
          <option value="1h">Last 1 Hour</option>
          <option value="24h">Last 24 Hours</option>
          <option value="7d">Last 7 Days</option>
        </select>

        <div className="logs-filter-checkbox">
          <input
            type="checkbox"
            id="known-users-filter"
            checked={knownUsersFilter}
            onChange={(e) => setKnownUsersFilter(e.target.checked)}
          />
          <label htmlFor="known-users-filter">Known Users Only</label>
        </div>

        <select
          value={pathFilter}
          onChange={(e) => setPathFilter(e.target.value)}
          className="logs-filter-select"
        >
          <option value="all">All Paths</option>
          {[...new Set(logs.map(log => log.path))].sort().map(path => (
            <option key={path} value={path}>{path}</option>
          ))}
        </select>

        <select
          value={durationFilter}
          onChange={(e) => setDurationFilter(e.target.value)}
          className="logs-filter-select"
        >
          <option value="all">All Durations</option>
          <option value="0-100">0-100 ms</option>
          <option value="100-500">100-500 ms</option>
          <option value="500-1000">500-1000 ms</option>
          <option value="1000+">1000+ ms</option>
        </select>

        <select
          value={flagFilter}
          onChange={(e) => setFlagFilter(e.target.value)}
          className="logs-filter-select"
        >
          <option value="all">All Flags</option>
          <option value="auth-failed">auth-failed</option>
          <option value="probe-404">probe-404</option>
          <option value="sql-probing">sql-probing</option>
          <option value="ua-missing">ua-missing</option>
        </select>

        <button
          onClick={() => {
            setLogSearchQuery("");
            setMethodFilter("all");
            setStatusFilter("all");
            setTimeFilter("all");
            setKnownUsersFilter(false);
            setPathFilter("all");
            setDurationFilter("all");
            setFlagFilter("all");
            setCurrentLogPage(1);
          }}
          className="logs-filter-reset"
        >
          Reset Filters
        </button>
      </div>

      {loadingLogs ? (
        <p>Loading logs...</p>
      ) : filteredLogs.length === 0 ? (
        <p>No logs match the current filters.</p>
      ) : (
        <>
          <div className="logs-grid-container">
            {(() => {
              const pathCounts = filteredLogs.reduce((acc, log) => {
                acc[log.path] = (acc[log.path] || 0) + 1;
                return acc;
              }, {});

              const pairedLogs = [];
              for (let i = 0; i < filteredLogs.length; i += 2) {
                pairedLogs.push(filteredLogs.slice(i, i + 2));
              }

              return (
                <div className="logs-grid">
                  {pairedLogs.map((pair, pairIndex) => (
                    <div key={pairIndex} className="log-pair">
                      {pair.map((log) => {
                        const ua = log.headers?.['user-agent'] || 'N/A';
                        const referer = log.headers?.['referer'] || 'N/A';
                        const geo = log.geo?.country || null;
                        const flags = log.suspicionFlags || [];
                        const isRepeatedPath = (pathCounts[log.path] || 0) > 5;

                        return (
                          <div
                            key={log._id}
                            className={`log-card ${isRepeatedPath ? "highlight-pattern" : ""}`}
                          >
                            <div className="log-header">
                              <span
                                className="log-method"
                                style={{
                                  backgroundColor:
                                    log.method === "GET"
                                      ? "#4CAF50"
                                      : log.method === "POST"
                                      ? "#2196F3"
                                      : log.method === "PUT"
                                      ? "#FF9800"
                                      : log.method === "DELETE"
                                      ? "#F44336"
                                      : "#9E9E9E",
                                }}
                              >
                                {log.method}
                              </span>
                              <span
                                className="log-status"
                                style={{
                                  backgroundColor:
                                    log.status >= 200 && log.status < 300
                                      ? "#4CAF50"
                                      : log.status >= 400 && log.status < 500
                                      ? "#FF9800"
                                      : log.status >= 500
                                      ? "#F44336"
                                      : "#9E9E9E",
                                }}
                                data-tooltip={statusCodeMessages[log.status] || `Status ${log.status}`}
                                aria-label={`Status ${log.status}: ${statusCodeMessages[log.status] || "Unknown status"}`}
                              >
                                {log.status}
                              </span>
                            </div>

                            <div className="log-content">
                              <div className="log-field">
                                <span className="log-label">Path:</span>
                                <span className="log-value">{log.path}</span>
                              </div>

                              <div className="log-field">
                                <span className="log-label">Timestamp:</span>
                                <span className="log-value">{new Date(log.timestamp).toLocaleString()}</span>
                              </div>

                              <div className="log-field">
                                <span className="log-label">Duration:</span>
                                <span className="log-value">{log.duration} ms</span>
                              </div>

                              <div className="log-field">
                                <span className="log-label">Bytes (req → res):</span>
                                <span className="log-value">
                                  {(log.bodyBytes ?? 0)} → {(log.responseBytes ?? 0)}
                                </span>
                              </div>

                              <div className="log-field">
                                <span className="log-label">IP:</span>
                                <span className="log-value">
                                  {log.ipAddress || 'N/A'}
                                  {log.username && (
                                    <span className="log-username">
                                      {' (User: '}
                                      <Link to={`/profile/${log.username}`} className="profile-link">
                                        {log.username}
                                      </Link>
                                      {')'}
                                    </span>
                                  )}
                                  {geo && <span className="log-geo"> • {geo}</span>}
                                </span>
                              </div>

                              <div className="log-field">
                                <span className="log-label">User-Agent:</span>
                                <span className="log-value" data-tooltip={ua}>
                                  {ua}
                                </span>
                              </div>

                              <div className="log-field">
                                <span className="log-label">Referer:</span>
                                <span className="log-value" title={referer}>
                                  {referer.length > 80 ? referer.slice(0, 80) + "…" : referer}
                                </span>
                              </div>

                              <div className="log-field">
                                <span className="log-label">Request ID:</span>
                                <span className="log-value">
                                  {log.requestId || 'N/A'}
                                  {log.requestId && (
                                    <button
                                      className="copy-btn"
                                      onClick={() => copyToClipboard(log.requestId)}
                                      aria-label="Copy Request ID"
                                      title="Copy Request ID"
                                    >
                                      Copy
                                    </button>
                                  )}
                                </span>
                              </div>

                              <div className="log-flags">
                                {(flags.length ? flags : ['—']).map((f, idx) => (
                                  <span key={idx} className={`badge badge-flag badge-${f.replace(/[^a-z-]/g,'')}`}>
                                    {f}
                                  </span>
                                ))}
                              </div>

                              <div className="headers-toggle-row">
                                <button
                                  className="headers-toggle"
                                  onClick={() => openHeadersModal(log._id)}
                                  aria-label="View Headers"
                                >
                                  View Headers
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {modalLogId && (
            <div className="headers-modal">
              <div className="headers-modal-content">
                <button
                  className="headers-modal-close"
                  onClick={closeHeadersModal}
                  aria-label="Close headers modal"
                >
                  ×
                </button>
                {filteredLogs.find(log => log._id === modalLogId) && (
                  <div className="headers-json">
                    <div className="headers-col">
                      <div className="headers-title">Request Headers</div>
                      <pre>{JSON.stringify(filteredLogs.find(log => log._id === modalLogId).headers || {}, null, 2)}</pre>
                    </div>
                    <div className="headers-col">
                      <div className="headers-title">Response Headers</div>
                      <pre>{JSON.stringify(filteredLogs.find(log => log._id === modalLogId).resHeaders || {}, null, 2)}</pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="pagination-controls">
            <button
              onClick={() => setCurrentLogPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentLogPage === 1}
              className="pagination-button"
            >
              Previous
            </button>
            <span className="pagination-text">
              Page {currentLogPage} of {totalPagesLogs}
            </span>
            <button
              onClick={() => setCurrentLogPage((prev) => Math.min(prev + 1, totalPagesLogs))}
              disabled={currentLogPage === totalPagesLogs}
              className="pagination-button"
            >
              Next
            </button>
            <div className="logs-per-page-selector">
              <label htmlFor="logs-per-page">Logs per page:</label>
              <select
                id="logs-per-page"
                value={logsPerPage}
                onChange={(e) => {
                  setLogsPerPage(Number(e.target.value));
                  setCurrentLogPage(1);
                }}
                className="logs-per-page-select"
              >
                <option value={4}>4 (2 pairs)</option>
                <option value={10}>10 (5 pairs)</option>
                <option value={20}>20 (10 pairs)</option>
                <option value={50}>50 (25 pairs)</option>
                <option value={100}>100 (50 pairs)</option>
              </select>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Logs;